export interface Row {
  [key: string | number]: any;
}

function ownEntries(value: any): Array<[string, any]> {
  return value == null ? [] : Object.entries(value);
}

function isObjectValue(value: any): boolean {
  return typeof value === 'object' && value !== null;
}

export function fixColNameForVega(name: string | number) {
  // custom charts can feed in numbers as column names
  // https://weightsandbiases.slack.com/archives/C01KQ5KTDC3/p1691760054418779
  const safeName = typeof name === 'number' ? String(name) : name;
  return safeName.replace(/\.\//g, '_');
}

export function flattenNested(rows: Row[]): Row[] {
  const result: Row[] = [];
  for (const row of rows) {
    // Flatten only nested objects (preserving nested arrays and basic values)
    // const flatObj = flattenNestedObjects(row);

    // Create a base Row from any non-array keys
    const baseRow: Row = {};
    const arrays: Row[][] = [];
    for (const [k, v] of ownEntries(row)) {
      if (Array.isArray(v)) {
        arrays.push(v);
      } else if (isObjectValue(v)) {
        Object.assign(baseRow, flattenNestedObjects(v));
      } else {
        baseRow[fixColNameForVega(k)] = v;
      }
    }

    // For each key with nested arrays, add a row for each unnested result,
    // including the keys from our base row.
    let added = false;
    for (const v of arrays) {
      for (const ir of flattenNested(v)) {
        result.push(Object.assign(ir, baseRow));
      }
      added = true;
    }
    if (added === false) {
      result.push(baseRow);
    }
  }
  return result;
}

// Given an object, flatten all sub-objects (not including arrays). Leave
// all other keys the same.
export function flattenNestedObjects(obj: Row): Row {
  const result: Row = {};
  for (const [k, v] of ownEntries(obj)) {
    if (Array.isArray(v)) {
      // drop arrays here for now
    } else if (isObjectValue(v)) {
      Object.assign(result, flattenNestedObjects(v));
    } else {
      result[fixColNameForVega(k)] = v;
    }
  }
  return result;
}

///// flattenNestedOld implements the old tableWithFullPathColNames transform

function concatPolicyFixKey(s: string) {
  // We probably don't need to remove the colon, but this policy
  // is only used for legacy panels so I left it.
  return s.replace(/:/g, '.').replace(/\./g, '_');
}

export function colNamePolicyConcat(parentKey: string, key: string) {
  return concatPolicyFixKey(parentKey) + '_' + concatPolicyFixKey(key);
}

export function flattenNestedOld(rows: Row[]): Row[] {
  const result: Row[] = [];
  for (const row of rows) {
    const baseRow: Row = {};

    // Flatten only nested objects (preserving nested arrays and basic values)
    const flatObj = flattenNestedObjectsOld(row);

    // Create a base Row from any non-array keys
    for (const [k, v] of ownEntries(flatObj)) {
      // k = fixKey(k);
      if (!Array.isArray(v)) {
        baseRow[k] = v;
      }
    }

    // For each key with nested arrays, add a row for each unnested result,
    // including the keys from our base row.
    let added = false;
    for (const [k, v] of ownEntries(flatObj)) {
      // k = fixKey(k);
      if (Array.isArray(v)) {
        for (const ir of flattenNestedOld(v)) {
          result.push({
            ...baseRow,
            ...Object.fromEntries(
              ownEntries(ir).map(([innerK, innerV]) => [
                colNamePolicyConcat(k, innerK),
                innerV,
              ])
            ),
          });
        }
        added = true;
      }
    }
    if (added === false) {
      result.push(baseRow);
    }
  }
  return result;
}

// Given an object, flatten all sub-objects (not including arrays). Leave
// all other keys the same.
export function flattenNestedObjectsOld(obj: Row): Row {
  const result: Row = {};
  for (const [k, v] of ownEntries(obj)) {
    // k = fixKey(k);
    if (isObjectValue(v) && !Array.isArray(v)) {
      for (const [sk, sv] of ownEntries(flattenNestedObjectsOld(v))) {
        // sk = fixKey(sk);
        result[colNamePolicyConcat(k, sk)] = sv;
      }
    } else {
      result[k] = v;
    }
  }
  return result;
}
